<ng-container
  *ngIf="type === 'singleScanner'; then singleScanner; else multipleScanners"
>
</ng-container>
<div
  class="clr-row"
  id="chart-controls"
  *ngIf="userHasSelectedScannersAndTrendKey"
>
  <div class="clr-col-6">
    <button
      type="button"
      class="btn btn-outline-primary"
      (click)="downloadData()"
    >
      Download Data
    </button>
  </div>
  <!--
    the x-axis view window; a fresh page always starts at All time and the
    choice is never stored (see TrendsComponent.rangePreset). Both tab
    instances of this component share the page, hence the type in the ids.
  -->
  <div class="clr-col-6 range-presets">
    <div
      class="btn-group btn-outline-primary btn-sm"
      role="group"
      aria-label="Date range shown"
    >
      <div class="radio btn" *ngFor="let preset of rangePresets">
        <input
          type="radio"
          [attr.name]="'range-preset-' + type"
          [id]="'range-preset-' + type + '-' + preset.key"
          [checked]="rangePreset === preset.key"
          (change)="setRangePreset(preset.key)"
        />
        <!--
          Clarity's ClrLabel directive (selector "label") owns the "for"
          attribute through its own host binding, so a plain [attr.for] is
          overwritten with nothing and the visible button never reaches the
          hidden radio. Bind the directive's input instead.
        -->
        <label [for]="'range-preset-' + type + '-' + preset.key">
          {{ preset.label }}
        </label>
      </div>
    </div>
  </div>
</div>
<div class="clr-row" *ngIf="noTestsInRange" id="no-tests-in-range-note">
  <div class="clr-col-12">
    <clr-alert [clrAlertType]="'info'" [clrAlertClosable]="false">
      <clr-alert-item>
        <span class="alert-text">
          No tests in the last {{ rangePresetLabel }}. Choose a wider range to
          see older tests.
        </span>
      </clr-alert-item>
    </clr-alert>
  </div>
</div>
<div
  class="clr-row"
  *ngIf="liveBaselineUnavailable"
  id="live-baseline-unavailable-note"
>
  <div class="clr-col-12">
    <clr-alert [clrAlertType]="'warning'" [clrAlertClosable]="false">
      <clr-alert-item>
        <span class="alert-text">
          Live baseline unavailable for
          {{ liveBaselineUnavailable.scannerNames.join(', ') }}; showing the
          limits stored with the latest test. Change any setting to retry.
        </span>
      </clr-alert-item>
    </clr-alert>
  </div>
</div>
<div class="clr-row" *ngIf="staleBaselineNote" id="stale-baseline-note">
  <div class="clr-col-12">
    <clr-alert [clrAlertType]="'info'" [clrAlertClosable]="false">
      <clr-alert-item>
        <span class="alert-text">
          {{ staleBaselineNote.staleCount }} of the
          {{ staleBaselineNote.testCount }} tests since the calibration on
          {{ staleBaselineNote.calibrationDate }} were evaluated against a
          previous baseline. The lines above show the current one; those tests
          can be re-evaluated on the
          <a [routerLink]="['/home/scanners', staleBaselineNote.scannerId]">{{
            'scanner page' | term
          }}</a
          >.
        </span>
      </clr-alert-item>
    </clr-alert>
  </div>
</div>

<ng-template #singleScanner>
  <div class="clr-row" id="scanner-selection">
    <div class="clr-col-4 dropdown-container">
      <app-scanner-multi-select
        class="dropdown-content"
        #scannerSelect
        (valueChange)="scannerSelectChange()"
        [scanners]="allScanners"
        [roomNames]="allRoomNames"
        [maxItems]="1"
        [title]="'Select a Scanner' | term"
      ></app-scanner-multi-select>
    </div>
    <div class="clr-col-4 dropdown-container">
      <app-side-controls
        [disabled]="!selectedScanner"
        class="dropdown-content"
        #sideControlLeft
        (valueChange)="sideControlChange()"
        [title]="'Select a measurement'"
        [position]="'left'"
        [phantomTestRoutines]="routineUnion"
        [surveyTestRoutines]="surveyTestRoutineUnion"
        [showYAxis]="false"
        [defaultTrendKey]="['materials', 'water', 'mean']"
      ></app-side-controls>
    </div>
    <div class="clr-col-4 dropdown-container">
      <app-side-controls
        class="dropdown-content"
        [disabled]="!firstParamSelected"
        #sideControlRight
        (valueChange)="sideControlChange()"
        [title]="'Select another (optional)'"
        [position]="'right'"
        [phantomTestRoutines]="routineUnion"
        [surveyTestRoutines]="surveyTestRoutineUnion"
      ></app-side-controls>
    </div>
  </div>
  <div class="clr-row" id="main-display">
    <div class="clr-col-12">
      <div
        *ngIf="chartOptions"
        class="chart"
        echarts
        [theme]="'dark'"
        [options]="chartOptions"
        [autoResize]="true"
        (chartClick)="onChartClick($event)"
        #echart
      ></div>
    </div>
  </div>
</ng-template>

<ng-template #multipleScanners>
  <div class="clr-row" id="scanner-selection">
    <div class="clr-col-6 dropdown-container">
      <app-scanner-multi-select
        #scannerSelect
        class="dropdown-content"
        (valueChange)="scannerSelectChange()"
        [scanners]="allScanners"
        [roomNames]="allRoomNames"
        [title]="'Select one or more Scanners' | term"
      ></app-scanner-multi-select>
    </div>
    <div class="clr-col-6 dropdown-container">
      <app-side-controls
        #sideControlLeft
        [disabled]="!selectedScanner"
        class="dropdown-content"
        (valueChange)="sideControlChange()"
        [title]="'Select a measurement'"
        [position]="'right'"
        [phantomTestRoutines]="routineUnion"
        [surveyTestRoutines]="surveyTestRoutineUnion"
        [showYAxis]="false"
        [defaultTrendKey]="['materials', 'water', 'mean']"
      ></app-side-controls>
    </div>
  </div>
  <div class="clr-row" id="main-display">
    <div class="clr-col-xl-12">
      <div
        *ngIf="chartOptions"
        class="chart"
        echarts
        [theme]="'dark'"
        [options]="chartOptions"
        [autoResize]="true"
        (chartClick)="onChartClick($event)"
        #echart
      ></div>
    </div>
  </div>
</ng-template>

<ng-template #chart>
  <div
    *ngIf="chartOptions"
    class="chart"
    echarts
    [theme]="'dark'"
    [options]="chartOptions"
    [autoResize]="true"
    (chartClick)="onChartClick($event)"
    #echart
  ></div>
</ng-template>
